// =====================================================================
// My Agency V2 — buttons (ARTBOARD-SPEC §4 CMP-BTN + CMP-ICONBTN).
// Purely presentational; all handlers/labels come in as props.
// =====================================================================
import React from "react";
import { OverflowDotsIcon } from "./icons";

// Desktop base: 13.5px/600, radius 9, padding 9px 16px.
// Mobile base: full-width, 15px/600, radius 12, padding 14px.
const VARIANTS = {
  primary: {
    base: "text-white bg-[#2283FF] border-0 shadow-[0_1px_2px_rgba(34,131,255,.35)] hover:bg-[#1670E6]",
    disabled: "text-white bg-[#A8C8F0] border-0 cursor-not-allowed",
  },
  secondary: {
    base: "text-[#475569] bg-white border border-[#DCE3EC] hover:border-[#B9C6D6]",
    disabled: "text-[#94A3B8] bg-white border border-[#DCE3EC] cursor-not-allowed",
  },
  danger: {
    base: "text-white bg-[#DC2626] border-0 shadow-[0_1px_2px_rgba(220,38,38,.35)] hover:bg-[#B91C1C]",
    disabled: "text-white bg-[#F1AEAE] border-0 cursor-not-allowed",
  },
  // TERTIARY-TINT (2g "Contact", 4b external CTA): 13px, tinted.
  tertiary: {
    base: "text-[13px] text-[#1668D9] bg-[#EAF3FF] border-0 hover:bg-[#DCEBFF]",
    disabled: "text-[13px] text-[#94A3B8] bg-[#EAEFF5] border-0 cursor-not-allowed",
  },
  // GHOST ("Show all N …" pagination): 13px/600 grey.
  ghost: {
    base: "text-[13px] text-[#475569] bg-white border border-[#DCE3EC] hover:border-[#B9C6D6] hover:text-[#0F172A]",
    disabled: "text-[13px] text-[#94A3B8] bg-white border border-[#DCE3EC] cursor-not-allowed",
  },
  // Mobile sheet-cancel: grey fill, no border.
  sheetCancel: {
    base: "text-[#334155] bg-[#F1F5F9] border-0",
    disabled: "text-[#94A3B8] bg-[#F1F5F9] border-0 cursor-not-allowed",
  },
};

const SIZE = {
  desktop: "text-[13.5px] font-semibold rounded-[9px] px-4 py-[9px]",
  desktopTertiary: "font-semibold rounded-[9px] px-[14px] py-2",
  desktopGhost: "font-semibold rounded-[9px] px-4 py-2",
  mobile: "w-full text-[15px] font-semibold rounded-xl p-3.5",
};

export const Button = ({
  variant = "primary",
  mobile = false,
  fab = false,
  icon = null,
  disabled = false,
  className = "",
  children,
  ...rest
}) => {
  const v = VARIANTS[variant] || VARIANTS.primary;
  const size = mobile
    ? SIZE.mobile
    : variant === "tertiary"
    ? SIZE.desktopTertiary
    : variant === "ghost"
    ? SIZE.desktopGhost
    : SIZE.desktop;
  return (
    <button
      type="button"
      disabled={disabled}
      className={[
        "whitespace-nowrap cursor-pointer font-tpv2",
        icon || mobile ? "inline-flex items-center justify-center gap-[7px]" : "",
        size,
        disabled ? v.disabled : v.base,
        fab ? "shadow-[0_8px_24px_rgba(34,131,255,.4)]" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
};

// CMP-ICONBTN — icon-only button (overflow dots, modal close, …).
// variant: "default" (30×30 r8) | "mobileClose" (30×30 circle grey) |
//          "mobileOverflow" (44×44 tap target) | "wallOverflow" (28×28
//          absolute top-right, dimmed until hover).
// `open` renders the active state (bg #EAF3FF; pair with icon fill
// #1668D9 at the call site or via OverflowButton below).
export const IconButton = ({
  variant = "default",
  open = false,
  className = "",
  style,
  children,
  ...rest
}) => {
  const shared =
    "flex-none flex items-center justify-center border-0 cursor-pointer";
  const byVariant = {
    default: `w-[30px] h-[30px] rounded-lg ${open ? "bg-[#EAF3FF]" : "bg-transparent hover:bg-[#F1F5F9]"}`,
    mobileClose: "w-[30px] h-[30px] rounded-full bg-[#F1F5F9]",
    mobileOverflow: `w-11 h-11 rounded-lg mt-[-7px] mr-[-10px] ${open ? "bg-[#EAF3FF]" : "bg-transparent"}`,
    wallOverflow: `w-7 h-7 rounded-[7px] absolute top-2 right-2 ${open ? "bg-[#EAF3FF] opacity-100" : "bg-transparent opacity-45 hover:opacity-100 hover:bg-[#F1F5F9]"}`,
  };
  return (
    <button
      type="button"
      className={[shared, byVariant[variant] || byVariant.default, className]
        .filter(Boolean)
        .join(" ")}
      style={style}
      {...rest}
    >
      {children}
    </button>
  );
};

// Convenience: overflow (kebab) button with correct open-state icon fill.
export const OverflowButton = ({ open = false, iconSize = 16, variant = "default", ...rest }) => (
  <IconButton variant={variant} open={open} {...rest}>
    <OverflowDotsIcon size={iconSize} fill={open ? "#1668D9" : "#64748B"} />
  </IconButton>
);
